import { useEffect, useState } from 'react';
import '../styles/Analytics.css';
import axios from 'axios';
import jsPDF from "jspdf";

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

function QRsPerProduct() {

  const baseURL = sessionStorage.getItem('baseURL');

  // chartData is stored in state so the React-rendered button can access it
  const [chartData, setChartData] = useState([]);

  /* ── Data helpers ─────────────────────────────────────────────────── */
  const handleFetchUniqueValues = (nestedArray) => {
    if (!nestedArray.length) return [];

    const occurrences = {};
    nestedArray.forEach(row => {
      const value    = row.sku_id;
      const skuValue = row.sku_name;
      occurrences[value] = {
        count: (occurrences[value]?.count || 0) + 1,
        sku:   skuValue,
        pu:    row.pu,
      };
    });

    return Object.entries(occurrences).map(([, dets]) => ({
      category: dets.sku,
      name:     dets.sku,
      pu:       dets.pu,
      value:    dets.count,
    }));
  };

  /* ── Chart builder ────────────────────────────────────────────────── */
  const callChart1 = async () => {
    try {
      const groupid = sessionStorage.getItem("isAdmin") === "true"
        ? "all"
        : sessionStorage.getItem("group_id");

      const skuResponse = groupid === "all"
        ? await axios.get(baseURL + "sku/fetchSku/all")
        : await axios.get(baseURL + `sku/fetchSku/${groupid}`);

      const skuRows = skuResponse.data;

      const qrResponses = await Promise.all(
        skuRows.map(row => axios.get(baseURL + `qr/fetchQR/${row.sku_id}`))
      );

      const qrs = qrResponses.flatMap((r, index) => {
        let d = r.data;
        for (let i = 0; i < d.length; i++) {
          d[i]['sku_name'] = skuRows[index].brand_name;
          d[i]['pu']       = skuRows[index].prod_unit;
        }
        return d;
      });

      const data = handleFetchUniqueValues(qrs);
      setChartData(data); // ← store for report button

      am4core.useTheme(am4themes_animated);

      let chart = am4core.create("chartdiv", am4charts.XYChart);
      chart.data = data;

      // X-AXIS
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "category";
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.tooltip.disabled = true;
      categoryAxis.renderer.labels.template.wrap     = true;
      categoryAxis.renderer.labels.template.truncate = false;

      // Y-AXIS
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.renderer.labels.template.adapter.add("text", text => {
        const n = Number(text);
        return Number.isInteger(n) ? n.toString() : "";
      });

      // SERIES
      let series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY    = "value";
      series.dataFields.categoryX = "category";
      series.tooltipText          = "{category}: {value}";
      series.columns.template.strokeWidth = 0;
      series.columns.template.column.cornerRadiusTopLeft  = 8;
      series.columns.template.column.cornerRadiusTopRight = 8;
      series.columns.template.adapter.add("fill", (fill, target) =>
        chart.colors.getIndex(target.dataItem.index)
      );

      // CURSOR
      chart.cursor         = new am4charts.XYCursor();
      chart.cursor.lineX.disabled = true;
      chart.cursor.lineY.disabled = true;

      return () => chart.dispose();

    } catch (error) {
      console.error("Chart-1 error:", error);
    }
  };

  /* ── PDF report (logic unchanged) ────────────────────────────────── */
  const downloadQRStatistics = async (data) => {
    const doc = new jsPDF("p", "mm", "a4");
    const PAGE_HEIGHT  = 297;
    const BOTTOM_MARGIN = 25;
    const minRowHeight  = 10;

    let colX = { no: 15, sku: 30, count: 150 };
    if (sessionStorage.getItem('isAdmin') === 'true') {
      colX = { no: 15, sku: 30, pu: 110, count: 165 };
    }

    let yPos = 0;

    const drawHeader = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, 210, 35, "F");
      try {
        doc.addImage("logo.png",   "PNG", 10,  5, 30, 25);
        doc.addImage("QRmor.jpeg", "PNG", 175, 5, 30, 25);
      } catch (e) {}
      doc.setTextColor(0);
      doc.setFontSize(22);
      doc.setFont(undefined, "bold");
      doc.text("Product-QR REPORT", 105, 15, { align: "center" });
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(
        `Generated: ${new Date().toLocaleString()} | ${sessionStorage.getItem("name") || ""}`,
        105, 25, { align: "center" }
      );
      if (sessionStorage.getItem("group_id") !== "null") {
        doc.text(sessionStorage.getItem("group_id"), 105, 30, { align: "center" });
      }
      doc.setTextColor(0);
      yPos = 45;
    };

    const drawTableHeader = () => {
      doc.setFontSize(10);
      doc.setFillColor(52, 73, 94);
      doc.rect(10, yPos - 7, 190, 10, "F");
      doc.setTextColor(255);
      doc.setFont(undefined, "bold");
      doc.text("No.",            colX.no,    yPos);
      doc.text("Product",        colX.sku,   yPos);
      if (sessionStorage.getItem('isAdmin') === 'true') {
        doc.text("Production Unit", colX.pu, yPos);
      }
      doc.text("QRs Generated",  colX.count, yPos);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0);
      yPos += minRowHeight;
    };

    drawHeader();
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Product-QR Details", 20, yPos);
    yPos += 10;
    drawTableHeader();

    data.forEach((row, index) => {
      const rowHeight = 10;
      if (yPos + rowHeight > PAGE_HEIGHT - BOTTOM_MARGIN) {
        doc.addPage();
        drawHeader();
        drawTableHeader();
      }
      if (index % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(10, yPos - 6, 190, rowHeight + 2, "F");
      }
      doc.text(String(index + 1), colX.no, yPos);
      const wrappedSku = doc.splitTextToSize(
        row.name,
        sessionStorage.getItem('isAdmin') === 'true'
          ? colX.pu    - colX.sku
          : colX.count - colX.sku
      );
      doc.text(wrappedSku, colX.sku, yPos);
      if (sessionStorage.getItem('isAdmin') === 'true') {
        doc.text(String(row.pu), colX.pu, yPos);
      }
      doc.text(String(row.value), colX.count + 10, yPos);
      yPos += rowHeight;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(130);
      doc.text("Confidential – Internal Use Only", 105, 285, { align: "center" });
      doc.text(`Page ${i} of ${pageCount}`,        105, 290, { align: "center" });
      doc.setTextColor(100);
      doc.text("QRmor Asset Security Analytics Report", 140, 280);
      doc.text("© 2026 Scinova Scientifics",            150, 285);
      doc.text("Powered by CIIRC®",                     155, 290);
    }

    if (yPos + 30 > PAGE_HEIGHT - BOTTOM_MARGIN) {
      doc.addPage();
      drawHeader();
    }
    doc.setFontSize(11);
    doc.text("End of Report",         105, yPos + 10, { align: "center" });
    doc.text("__________________________", 140, yPos + 25);
    doc.text("Authorized Signatory",       150, yPos + 32);

    const fileName = `QR_Generation_Analytics_Report-${new Date().toISOString().slice(0, 10)}.pdf`;
    const pdfBlob  = doc.output("blob");
    const pdfFile  = new File([pdfBlob], fileName, { type: "application/pdf" });
    window.open(URL.createObjectURL(pdfFile), "_blank");
  };

  /* ── Mount ────────────────────────────────────────────────────────── */
  useEffect(() => {
    callChart1();
  }, []);

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <div className="chart-container">
      {/* React-rendered button — no more imperative DOM injection */}
      <div className="chart-actions">
        <button
          className="chart-action-btn"
          onClick={() => downloadQRStatistics(chartData)}
          disabled={!chartData.length}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          View Statistics
        </button>
      </div>

      <div id="chartdiv" style={{ width: "100%", height: "65vh" }} />
    </div>
  );
}

export default QRsPerProduct;